import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { FiPlus, FiX, FiChevronLeft, FiChevronRight } from 'react-icons/fi';
import { storiesAPI } from '../utils/api';
import { useAuth } from '../context/AuthContext';
import toast from 'react-hot-toast';

export default function StoriesBar() {
  const { user }              = useAuth();
  const [groups, setGroups]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewing, setViewing] = useState(null);
  const fileRef               = useRef();
  const timerRef              = useRef();

  useEffect(() => {
    storiesAPI.getFeed()
      .then(r => setGroups(r.data.story_groups || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const openStory = (gi, si = 0) => {
    setViewing({ gi, si });
    const story = groups[gi]?.stories[si];
    if (story) storiesAPI.viewStory(story.id).catch(() => {});
    startTimer(gi, si);
  };

  const startTimer = (gi, si) => {
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => nextStory(gi, si), 5000);
  };

  const nextStory = (gi, si) => {
    const g = groups[gi];
    if (!g) return closeViewer();
    if (si + 1 < g.stories.length) {
      const nsi = si + 1;
      setViewing({ gi, si: nsi });
      storiesAPI.viewStory(g.stories[nsi].id).catch(() => {});
      startTimer(gi, nsi);
    } else if (gi + 1 < groups.length) {
      openStory(gi + 1, 0);
    } else {
      closeViewer();
    }
  };

  const prevStory = (gi, si) => {
    clearTimeout(timerRef.current);
    if (si > 0) { setViewing({ gi, si: si - 1 }); startTimer(gi, si - 1); }
    else if (gi > 0) openStory(gi - 1, 0);
  };

  const closeViewer = () => { clearTimeout(timerRef.current); setViewing(null); };

  const handleUpload = async e => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append('media', file);
    const tid = toast.loading('Uploading story…');
    try {
      await storiesAPI.createStory(fd);
      toast.success('Story posted!', { id: tid });
      const r = await storiesAPI.getFeed();
      setGroups(r.data.story_groups || []);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed.', { id: tid });
    }
    e.target.value = '';
  };

  const avatarPh = (name, size) => (
    <div className="story-avatar-ph" style={{ width: size, height: size }}>
      {name?.[0]?.toUpperCase()}
    </div>
  );

  const currentGroup = viewing ? groups[viewing.gi] : null;
  const currentStory = currentGroup?.stories[viewing?.si];

  return (
    <>
      <div className="stories-bar">
        {/* Add story */}
        <div className="story-item">
          <button className="add-story-wrap" onClick={() => fileRef.current?.click()}>
            <div className="story-ring-wrap seen">
              {user?.avatar
                ? <img src={user.avatar} alt="" className="avatar story-avatar-size" />
                : avatarPh(user?.full_name, undefined)
              }
            </div>
            <span className="add-icon"><FiPlus size={11} /></span>
          </button>
          <span className="story-name">Your story</span>
          <input ref={fileRef} type="file" accept="image/*,video/*" hidden onChange={handleUpload} />
        </div>

        {/* Story groups */}
        {loading
          ? Array(5).fill(0).map((_, i) => (
              <div key={i} className="story-item">
                <div className="skeleton story-avatar-size" style={{ borderRadius: '50%' }} />
                <div className="skeleton" style={{ width: 44, height: 8 }} />
              </div>
            ))
          : groups.map((g, gi) => (
              <div key={g.user_id} className="story-item" onClick={() => openStory(gi)}>
                <div className={`story-ring-wrap ${g.has_new ? 'has-new' : 'seen'}`}>
                  {g.avatar
                    ? <img src={g.avatar} alt="" className="avatar story-avatar-size" />
                    : avatarPh(g.username, undefined)
                  }
                </div>
                <span className="story-name">{g.username}</span>
              </div>
            ))
        }
      </div>

      {/* Story viewer */}
      {viewing && currentStory && (
        <div className="story-viewer" onClick={closeViewer}>
          <div className="story-viewer-inner" onClick={e => e.stopPropagation()}>
            <div className="story-progress-bars">
              {currentGroup.stories.map((_, i) => (
                <div key={i} className="story-progress-track">
                  <div className={`story-progress-fill${i < viewing.si ? ' done' : i === viewing.si ? ' active' : ''}`} />
                </div>
              ))}
            </div>

            <div className="story-header">
              <Link to={`/profile/${currentGroup.user_id}`} style={{ display: 'flex', alignItems: 'center', gap: 10, textDecoration: 'none' }} onClick={closeViewer}>
                {currentGroup.avatar
                  ? <img src={currentGroup.avatar} className="avatar" style={{ width: 36, height: 36, border: '2px solid #fff' }} alt="" />
                  : <div style={{ width: 36, height: 36, borderRadius: '50%', background: 'rgba(255,255,255,.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontWeight: 700 }}>
                      {currentGroup.username?.[0]}
                    </div>
                }
                <span style={{ color: '#fff', fontWeight: 700, fontSize: 'var(--fs-sm)' }}>{currentGroup.username}</span>
              </Link>
              <button onClick={closeViewer} style={{ color: '#fff', marginLeft: 'auto', background: 'none', border: 'none', cursor: 'pointer', display: 'flex', padding: 6 }}>
                <FiX size={20} />
              </button>
            </div>

            {currentStory.media_type === 'video'
              ? <video src={currentStory.media_url} autoPlay muted loop playsInline
                  style={{ width: '100%', height: '100%', objectFit: 'cover', position: 'absolute', inset: 0 }} />
              : <img src={currentStory.media_url} alt=""
                  style={{ width: '100%', height: '100%', objectFit: 'cover', position: 'absolute', inset: 0, zIndex: 0 }} />
            }

            {currentStory.caption && (
              <div style={{ position: 'absolute', bottom: 44, left: 0, right: 0, padding: '0 20px', zIndex: 2, textAlign: 'center' }}>
                <p style={{ color: '#fff', fontSize: 'var(--fs-base)', textShadow: '0 1px 6px rgba(0,0,0,.7)' }}>
                  {currentStory.caption}
                </p>
              </div>
            )}

            <button className="story-nav prev" onClick={e => { e.stopPropagation(); prevStory(viewing.gi, viewing.si); }}>
              <FiChevronLeft size={26} />
            </button>
            <button className="story-nav next" onClick={e => { e.stopPropagation(); nextStory(viewing.gi, viewing.si); }}>
              <FiChevronRight size={26} />
            </button>
          </div>
        </div>
      )}
    </>
  );
}
